import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * InstitutionalOnboardingService - Handles institutional email domains and bulk invitations
 *
 * Features:
 * - Auto-join based on email domain
 * - Bulk invitation codes with usage limits
 * - Auto-approval for trusted domains
 * - Concurrent invitation handling
 */
class InstitutionalOnboardingService {
  /**
   * Check if an email belongs to an institutional domain and auto-join if configured
   * @param {string} email - User's email address
   * @param {string} userId - User's Firebase Auth UID
   * @returns {Promise<Object|null>} School data if auto-joined, null otherwise
   */
  async checkInstitutionalEmail(email, userId) {
    if (!email) return null;

    try {
      // Extract domain from email
      const domain = email.split('@')[1]?.toLowerCase();
      if (!domain) return null;

      console.log('[InstitutionalOnboarding] Checking domain:', domain);

      // Query schools with this institutional domain
      const schoolsRef = collection(db, 'schools');
      const q = query(schoolsRef, where('institutionalDomains', 'array-contains', domain));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log('[InstitutionalOnboarding] No institutional match for domain:', domain);
        return null;
      }

      // Get the first matching school (there should only be one per domain)
      const schoolDoc = snapshot.docs[0];
      const schoolData = { id: schoolDoc.id, ...schoolDoc.data() };

      console.log('[InstitutionalOnboarding] Found institutional school:', schoolData.name);

      // Check if auto-join is enabled
      if (!schoolData.autoJoinEnabled) {
        console.log('[InstitutionalOnboarding] Auto-join disabled for school:', schoolData.name);
        return null;
      }

      // Auto-join the user to this school
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        schoolId: schoolDoc.id,
        assignedSchoolId: schoolDoc.id,
        schoolIds: arrayUnion(schoolDoc.id),
        institutionalJoin: true,
        institutionalDomain: domain,
        joinedAt: serverTimestamp(),
        // Auto-approve institutional users as staff — school-scoped role
        [`schoolRoles.${schoolDoc.id}`]: {
          accessRole: 'staff',
          customRoleId: null,
          grantedAt: serverTimestamp(),
          grantedBy: 'institutional_auto_join'
        }
      });

      console.log('[InstitutionalOnboarding] ✅ User auto-joined to school:', schoolData.name);

      // Log the auto-join event
      await this.logOnboardingEvent(userId, schoolDoc.id, 'institutional_auto_join', {
        email,
        domain,
        schoolName: schoolData.name
      });

      return schoolData;
    } catch (error) {
      console.error('[InstitutionalOnboarding] Error checking institutional email:', error);
      return null;
    }
  }

  /**
   * Create a bulk invitation code for a school
   * @param {string} schoolId - School ID
   * @param {Object} options - Invitation options
   * @returns {Promise<Object>} Created invitation code data
   */
  async createBulkInvitationCode(schoolId, options = {}) {
    const {
      code = this.generateInviteCode(),
      maxUses = 100,
      expiresInDays = 30,
      autoApprove = false,
      accessRole = 'staff',
      customRoleId = null,
      createdBy,
      createdByName,
      description = ''
    } = options;

    try {
      // Check if code already exists
      const existingCode = await this.getInvitationCode(schoolId, code);
      if (existingCode) {
        throw new Error(`Invitation code "${code}" already exists`);
      }

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiresInDays);

      const inviteData = {
        code: code.toUpperCase(),
        schoolId,
        type: 'bulk',
        maxUses,
        currentUses: 0,
        usedBy: [], // Array of user IDs who used this code
        autoApprove,
        accessRole,
        customRoleId,
        active: true,
        expiresAt: Timestamp.fromDate(expiresAt),
        createdAt: serverTimestamp(),
        createdBy: createdBy || 'system',
        createdByName: createdByName || 'System',
        description
      };

      // Store in school's bulkInvitations subcollection
      const inviteRef = doc(db, 'schools', schoolId, 'bulkInvitations', code.toUpperCase());
      await setDoc(inviteRef, inviteData);

      console.log('[InstitutionalOnboarding] Created bulk invitation code:', code);
      return inviteData;
    } catch (error) {
      console.error('[InstitutionalOnboarding] Error creating bulk invitation:', error);
      throw error;
    }
  }

  /**
   * Use a bulk invitation code to join a school
   * @param {string} code - Invitation code
   * @param {string} userId - User's Firebase Auth UID
   * @param {Object} userData - Additional user data (email, name, etc.)
   * @returns {Promise<Object>} Result of using the invitation
   */
  async useBulkInvitationCode(code, userId, userData = {}) {
    if (!code || !userId) {
      throw new Error('Code and userId are required');
    }

    const upperCode = code.toUpperCase();

    try {
      // Find the invitation code across all schools
      const schoolsSnapshot = await getDocs(collection(db, 'schools'));
      let invitation = null;
      let schoolId = null;

      for (const schoolDoc of schoolsSnapshot.docs) {
        const inviteRef = doc(db, 'schools', schoolDoc.id, 'bulkInvitations', upperCode);
        const inviteSnap = await getDoc(inviteRef);

        if (inviteSnap.exists()) {
          invitation = { id: inviteSnap.id, ...inviteSnap.data() };
          schoolId = schoolDoc.id;
          break;
        }
      }

      if (!invitation) {
        throw new Error('Invalid invitation code');
      }

      // Validate invitation
      const validation = this.validateInvitation(invitation);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Check if user already used this code
      if (invitation.usedBy.includes(userId)) {
        throw new Error('You have already used this invitation code');
      }

      // Get school data
      const schoolRef = doc(db, 'schools', schoolId);
      const schoolSnap = await getDoc(schoolRef);
      const schoolData = { id: schoolSnap.id, ...schoolSnap.data() };

      // Update user document with school-scoped role (no global staff/admin flags)
      const userRef = doc(db, 'users', userId);
      const updateData = {
        schoolId,
        assignedSchoolId: schoolId,
        schoolIds: arrayUnion(schoolId),
        [`schoolRoles.${schoolId}`]: {
          accessRole: invitation.accessRole ?? 'staff',
          customRoleId: invitation.customRoleId ?? null,
          grantedAt: serverTimestamp(),
          grantedBy: 'bulk_invitation'
        },
        setupComplete: true,
        joinedVia: 'bulk_invitation',
        invitationCode: upperCode,
        joinedAt: serverTimestamp()
      };

      // If auto-approve is enabled, mark as approved immediately
      if (invitation.autoApprove) {
        updateData.approvedAt = serverTimestamp();
        updateData.autoApproved = true;
      }

      await updateDoc(userRef, updateData);

      // Update invitation usage
      const inviteRef = doc(db, 'schools', schoolId, 'bulkInvitations', upperCode);
      await updateDoc(inviteRef, {
        currentUses: increment(1),
        usedBy: arrayUnion(userId),
        lastUsedAt: serverTimestamp()
      });

      // Log the event
      await this.logOnboardingEvent(userId, schoolId, 'bulk_invitation_used', {
        code: upperCode,
        autoApproved: invitation.autoApprove,
        email: userData.email
      });

      return {
        success: true,
        school: schoolData,
        autoApproved: invitation.autoApprove,
        message: invitation.autoApprove
          ? `Welcome to ${schoolData.name}! You've been automatically approved.`
          : `You've joined ${schoolData.name}. An admin will review your access shortly.`
      };

    } catch (error) {
      console.error('[InstitutionalOnboarding] Error using bulk invitation:', error);
      throw error;
    }
  }

  /**
   * Get all pending approvals for a school (for queue system)
   * @param {string} schoolId - School ID
   * @returns {Promise<Array>} Array of pending users
   */
  async getPendingApprovals(schoolId) {
    try {
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('schoolId', '==', schoolId),
        where('approvedAt', '==', null)
      );

      const snapshot = await getDocs(q);
      const pendingUsers = [];

      snapshot.forEach(doc => {
        const userData = doc.data();
        // Only include users who joined via invitation (not auto-approved)
        if (userData.joinedVia && !userData.autoApproved) {
          pendingUsers.push({
            id: doc.id,
            ...userData
          });
        }
      });

      // Sort by join date (oldest first)
      pendingUsers.sort((a, b) => {
        const aTime = a.joinedAt?.toMillis() || 0;
        const bTime = b.joinedAt?.toMillis() || 0;
        return aTime - bTime;
      });

      return pendingUsers;
    } catch (error) {
      console.error('[InstitutionalOnboarding] Error getting pending approvals:', error);
      throw error;
    }
  }

  /**
   * Bulk approve multiple users
   * @param {string} schoolId - School ID
   * @param {Array<string>} userIds - Array of user IDs to approve
   * @param {Object} approver - Approver details
   * @returns {Promise<Object>} Approval results
   */
  async bulkApproveUsers(schoolId, userIds, approver = {}) {
    const results = {
      approved: [],
      failed: []
    };

    for (const userId of userIds) {
      try {
        const userRef = doc(db, 'users', userId);
        // Fetch user to get their assignedSchoolId (the school they actually joined)
        const userSnap = await getDoc(userRef);
        const targetSchoolId = userSnap.data()?.assignedSchoolId || schoolId;
        await updateDoc(userRef, {
          approvedAt: serverTimestamp(),
          approvedBy: approver.id || 'system',
          approvedByName: approver.name || 'System',
          [`schoolRoles.${targetSchoolId}`]: {
            accessRole: 'staff',
            customRoleId: null,
            grantedAt: serverTimestamp(),
            grantedBy: approver.id || 'system'
          }
        });

        results.approved.push(userId);

        // Log approval
        await this.logOnboardingEvent(userId, schoolId, 'bulk_approved', {
          approvedBy: approver.name || 'System'
        });

      } catch (error) {
        console.error(`[InstitutionalOnboarding] Failed to approve user ${userId}:`, error);
        results.failed.push({ userId, error: error.message });
      }
    }

    return results;
  }

  /**
   * Configure institutional domains for a school
   * @param {string} schoolId - School ID
   * @param {Array<string>} domains - Array of email domains
   * @param {boolean} autoJoinEnabled - Enable auto-join for these domains
   * @returns {Promise<void>}
   */
  async configureInstitutionalDomains(schoolId, domains, autoJoinEnabled = true) {
    try {
      // Normalize domains (lowercase, no @ symbol)
      const normalizedDomains = domains.map(d =>
        d.toLowerCase().replace('@', '').trim()
      ).filter(Boolean);

      // Check for conflicts with other schools
      for (const domain of normalizedDomains) {
        const conflictingSchool = await this.findSchoolByDomain(domain);
        if (conflictingSchool && conflictingSchool.id !== schoolId) {
          throw new Error(
            `Domain "${domain}" is already registered to ${conflictingSchool.name}`
          );
        }
      }

      // Update school document
      const schoolRef = doc(db, 'schools', schoolId);
      await updateDoc(schoolRef, {
        institutionalDomains: normalizedDomains,
        autoJoinEnabled,
        institutionalConfigUpdatedAt: serverTimestamp()
      });

      console.log('[InstitutionalOnboarding] Configured domains for school:', normalizedDomains);
    } catch (error) {
      console.error('[InstitutionalOnboarding] Error configuring domains:', error);
      throw error;
    }
  }

  // Helper Methods

  /**
   * Generate a random invitation code
   * @returns {string} 6-character alphanumeric code
   */
  generateInviteCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }

  /**
   * Validate an invitation
   * @param {Object} invitation - Invitation data
   * @returns {Object} Validation result
   */
  validateInvitation(invitation) {
    if (!invitation.active) {
      return { valid: false, error: 'This invitation code is no longer active' };
    }

    if (invitation.expiresAt) {
      const expiresMs = invitation.expiresAt.toMillis
        ? invitation.expiresAt.toMillis()
        : invitation.expiresAt.toDate().getTime();
      if (expiresMs < Date.now()) {
        return { valid: false, error: 'This invitation code has expired' };
      }
    }

    if (invitation.maxUses && invitation.currentUses >= invitation.maxUses) {
      return { valid: false, error: 'This invitation code has reached its usage limit' };
    }

    return { valid: true };
  }

  /**
   * Get an invitation code
   * @param {string} schoolId - School ID
   * @param {string} code - Invitation code
   * @returns {Promise<Object|null>} Invitation data or null
   */
  async getInvitationCode(schoolId, code) {
    try {
      const inviteRef = doc(db, 'schools', schoolId, 'bulkInvitations', code.toUpperCase());
      const inviteSnap = await getDoc(inviteRef);

      if (inviteSnap.exists()) {
        return { id: inviteSnap.id, ...inviteSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('[InstitutionalOnboarding] Error getting invitation:', error);
      return null;
    }
  }

  /**
   * Find a school by institutional domain
   * @param {string} domain - Email domain
   * @returns {Promise<Object|null>} School data or null
   */
  async findSchoolByDomain(domain) {
    try {
      const schoolsRef = collection(db, 'schools');
      const q = query(schoolsRef, where('institutionalDomains', 'array-contains', domain));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('[InstitutionalOnboarding] Error finding school by domain:', error);
      return null;
    }
  }

  /**
   * Log an onboarding event for analytics
   * @param {string} userId - User ID
   * @param {string} schoolId - School ID
   * @param {string} eventType - Type of event
   * @param {Object} metadata - Additional event data
   */
  async logOnboardingEvent(userId, schoolId, eventType, metadata = {}) {
    try {
      const eventRef = doc(collection(db, 'onboardingEvents'));
      await setDoc(eventRef, {
        userId,
        schoolId,
        eventType,
        metadata,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      // Log but don't throw - this is non-critical
      console.warn('[InstitutionalOnboarding] Failed to log event:', error);
    }
  }
}

export default new InstitutionalOnboardingService();